/* My gang sheets: everything saved in this browser (and, signed in by email, everything saved to that
   email: 0.3e Stage B). Search and filter to find one; each card opens it, copies it, removes it from the
   email, or deletes it. */

import { useEffect, useMemo, useState } from 'react';
import { formatMoney, formatDims } from '@gsb/core';
import { api, type ProjectListItem } from '../api';
import { navigate } from '../router';
import { Icon } from '../ui/icons';
import { Dialog, Seg } from '../ui/primitives';
import { toast, startFresh, useStore, emailOn, openEmailFlow, editCopy } from '../store/store';
import { SignedInLine, RemoveDialog } from '../ui/Email';

type Filter = 'all' | 'draft' | 'ordered';

export function Mine() {
  const [list, setList] = useState<ProjectListItem[] | null>(null);
  const [del, setDel] = useState<ProjectListItem | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [unlink, setUnlink] = useState<ProjectListItem | null>(null);
  const email = useStore(s => (s.config?.email?.mode === 'on' ? s.email : null));
  const load = () => api.list().then(r => setList(r.projects)).catch(e => { toast(e.message, { bad: true }); setList([]); });
  // signing in or out by email changes the list
  useEffect(() => { void load(); }, [email?.signedIn, email?.email]);

  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (list || []).filter(p => {
      if (filter === 'draft' && p.status !== 'draft') return false;
      if (filter === 'ordered' && p.status === 'draft') return false;
      const hay = (p.name + ' ' + (p.summary.transfer === 'uv' ? 'uv dtf' : 'dtf')).toLowerCase();
      return words.every(w => hay.includes(w));
    });
  }, [list, q, filter]);
  const counts = useMemo(() => ({
    all: list?.length || 0,
    draft: list?.filter(p => p.status === 'draft').length || 0,
    ordered: list?.filter(p => p.status !== 'draft').length || 0
  }), [list]);

  const open = (p: ProjectListItem) => navigate('/p/' + p.id);
  const duplicate = async (p: ProjectListItem) => {
    setBusy(p.id);
    try { await api.duplicate(p.id); toast('Copied “' + p.name + '”. Open the copy to change it or order it again.'); await load(); }
    catch (e) { toast((e as Error).message, { bad: true }); }
    finally { setBusy(null); }
  };

  return (
    <main className="mine">
      <div className="mine-in">
        <div className="mine-head">
          <div className="grow">
            <span className="kicker">Your saved work</span>
            <h1 className="h1">My gang sheets</h1>
            <p className="muted">{email?.signedIn
              ? 'Saved in this browser, plus the gangsheets saved to your email. Open one to keep editing, copy it to order again, or tidy up.'
              : 'Saved automatically in this browser. Open one to keep editing, copy it to order again, or tidy up.'}</p>
            {email?.signedIn && <SignedInLine className="mine-signed" />}
          </div>
          <div className="mine-head-acts">
            <button className="btn btn-line" onClick={() => { const id = useStore.getState().projectId; navigate(id ? '/p/' + id : '/'); }}><Icon name="arrowLeft" /><span className="no-phone">Back to the builder</span><span className="only-phone">Builder</span></button>
            <button className="btn btn-primary" onClick={() => void startFresh({ over: false })}><Icon name="plus" />New gangsheet</button>
          </div>
        </div>

        {email && !email.signedIn && (
          <div className="callout callout-info mine-find" data-mine="find-note">
            <Icon name="info" />
            <span>
              <b>Can’t see a gangsheet?</b> These are saved in this browser only. Clearing your browser data, private browsing or a different device means you won’t see them here, and a gangsheet’s link on its own won’t bring it back. If you saved one to your email, Find My Gangsheets opens it here.
            </span>
            <button className="btn btn-primary btn-sm" onClick={() => openEmailFlow('find')} data-mine="find"><Icon name="search" />Find My Gangsheets</button>
          </div>
        )}
        {list === null ? <p className="muted" style={{ marginTop: 24 }}><span className="spin" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />Loading…</p>
          : !list.length ? (
            <div className="card mine-empty">
              <span className="drop-ico"><Icon name="sheets" /></span>
              <h2 className="h3">No gang sheets yet</h2>
              <p className="muted small">Upload your artwork and your gangsheet is saved here as you go.</p>
              <div className="em-row">
                <button className="btn btn-primary btn-sm" onClick={() => void startFresh({ over: false })}><Icon name="plus" />Start a gangsheet</button>
                {email && !email.signedIn && <button className="btn btn-line btn-sm" onClick={() => openEmailFlow('find')}><Icon name="search" />Find My Gangsheets</button>}
              </div>
            </div>
          ) : (
            <>
              <div className="mine-tools">
                <label className="search">
                  <Icon name="search" />
                  <input className="in" type="search" placeholder="Find a gangsheet by name" aria-label="Find a gangsheet by name" value={q} onChange={e => setQ(e.target.value)} />
                </label>
                <Seg<Filter> label="Show" value={filter} onChange={setFilter} options={[
                  { value: 'all', label: 'All (' + counts.all + ')' },
                  { value: 'draft', label: 'Drafts (' + counts.draft + ')' },
                  { value: 'ordered', label: 'Ordered (' + counts.ordered + ')' }
                ]} />
              </div>
              {!shown.length ? (
                <p className="muted mine-none">Nothing matches{q ? ' “' + q + '”' : ''}. <button className="link" onClick={() => { setQ(''); setFilter('all'); }}>Show all</button></p>
              ) : (
                <ul className="mgrid">
                  {shown.map(p => (
                    <li key={p.id} className="mcard">
                      <div className="pic-wrap">
                        <button className="pic" onClick={() => open(p)} aria-label={'Open ' + p.name}>
                          <img src={p.thumbUrl} alt="" loading="lazy" onError={e => ((e.target as HTMLImageElement).style.visibility = 'hidden')} />
                        </button>
                        <span className="pic-tag">{status(p.status)}</span>
                        <span className="pic-acts">
                          <button className="ibtn ibtn-sm" aria-label={'Duplicate ' + p.name} data-tip="Duplicate (to order again or change)" data-tip-pos="left" disabled={busy === p.id} onClick={() => void duplicate(p)}>
                            {busy === p.id ? <span className="spin" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <Icon name="copy" />}
                          </button>
                          {email && p.savedTo && <button className="ibtn ibtn-sm" aria-label={'Remove ' + p.name + ' from my email'} data-tip="Remove from my email (nothing is deleted)" data-tip-pos="left" onClick={() => setUnlink(p)}><Icon name="mailOff" /></button>}
                          <button className="ibtn ibtn-sm is-danger" aria-label={'Delete ' + p.name} data-tip="Delete" data-tip-pos="left" onClick={() => setDel(p)}><Icon name="trash" /></button>
                        </span>
                      </div>
                      <div className="body">
                        <b className="mname" title={p.name}>{p.name}</b>
                        <span className="small muted">
                          {(p.summary.transfer === 'uv' ? 'UV DTF' : 'DTF')} · {p.summary.sheets?.length ? p.summary.sheets.map(s => formatDims(s.widthMm, s.lengthMm, 'cm')).join(' + ') : p.summary.designs + ' design' + (p.summary.designs === 1 ? '' : 's') + ', not built yet'}
                        </span>
                        <span className="small muted">
                          {p.summary.transfers} transfer{p.summary.transfers === 1 ? '' : 's'}{p.summary.price != null ? <> · <b className="mprice">{formatMoney(p.summary.price)}</b></> : null}
                        </span>
                        <span className="tiny muted">Edited {edited(p.updatedAt)}</span>
                        {email && (p.savedTo
                          ? <span className="tiny mwhere is-email" title={'Saved to ' + p.savedTo}><Icon name="mail" />Saved to email{p.madeHere === false ? ' · made on another device' : ''}</span>
                          : <span className="tiny mwhere"><Icon name="info" />Only in this browser</span>)}
                      </div>
                      <div className="acts">
                        {p.orderedAt
                          ? <button className="btn btn-primary btn-sm btn-block mopen" onClick={() => void editCopy(p.id)} data-mine="copy">Edit a copy<Icon name="copy" className="ico-arrow" /></button>
                          : <button className="btn btn-primary btn-sm btn-block mopen" onClick={() => open(p)}>Continue editing<Icon name="arrowRight" className="ico-arrow" /></button>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
      </div>
      {del && (
        <Dialog title="Delete gangsheet" onClose={() => setDel(null)} className="del-dlg">
          <h2>Delete “{del.name}”?</h2>
          {email && del.savedTo ? (
            <p className="muted small">
              It’s removed from My gang sheets everywhere it’s saved: {del.madeHere === false ? 'in the browser it was made in, ' : 'in this browser, '}and on every device signed in to {del.savedTo}. Orders you’ve already sent aren’t affected.
              {del.madeHere === false ? <> <b>It was made on another device.</b></> : null}
            </p>
          ) : (
            <p className="muted small">It’s removed from this list. Orders you’ve already sent aren’t affected.</p>
          )}
          {email && del.savedTo && <p className="small"><button className="link small" onClick={() => { setUnlink(del); setDel(null); }}><Icon name="mailOff" />Only take it off your email instead (nothing is deleted)</button></p>}
          <div className="dialog-actions">
            <button className="btn btn-line btn-sm" onClick={() => setDel(null)}>Keep it</button>
            <button className="btn btn-danger btn-sm" data-mine="delete-confirm" onClick={async () => { try { await api.remove(del.id); toast('Deleted.'); } catch (e) { toast((e as Error).message, { bad: true }); } setDel(null); void load(); }}><Icon name="trash" />{del.madeHere === false ? 'Delete everywhere' : 'Delete'}</button>
          </div>
        </Dialog>
      )}
      {unlink && unlink.savedTo && <RemoveDialog id={unlink.id} name={unlink.name} madeHere={unlink.madeHere !== false} savedTo={unlink.savedTo} onClose={() => { setUnlink(null); void load(); }} />}
    </main>
  );
}

function status(s: string) {
  return s === 'submitted' ? <span className="chip chip-cyan">Sent to order</span> : s === 'ordered' ? <span className="chip chip-green">Ordered</span> : <span className="chip">Draft</span>;
}

/** "today, 3:41 pm", "yesterday", "8 Oct", "8 Oct 2025" */
function edited(iso: string): string {
  const d = new Date(iso), now = new Date();
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  if (diff === 0) return 'today, ' + d.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' });
  if (diff === 1) return 'yesterday';
  return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}
